// Webinar registration page (sections/mvd-webinar.liquid): calendar reminder and Kit form enhancement.
// Ported from the Manus page (release cddc92cb). Settings arrive as data-* attributes on [data-mvd-webinar].
// Works on first load and when the theme editor re-renders the section, without duplicate
// observers, listeners or Kit scripts.

const PHONE_LIBRARY = 'https://cdn.jsdelivr.net/npm/libphonenumber-js@1.11.18/+esm';
const KIT_SCRIPT = (uid) => `https://mortenvetdentistry.kit.com/${uid}/index.js`;

const observers = new WeakMap();
let phoneLibrary;
let countryOptions;

function loadPhoneLibrary() {
  phoneLibrary ||= import(PHONE_LIBRARY).then((lib) => {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    countryOptions = lib.getCountries()
      .map((country) => ({ country, name: regionNames.of(country), code: lib.getCountryCallingCode(country) }))
      .filter(({ name }) => Boolean(name))
      .sort((left, right) => left.name.localeCompare(right.name));
    return lib;
  });
  return phoneLibrary;
}

/* Calendar reminder: built from the event start in Theme settings → Webinar, in UTC, with the
   original 15-minute alarm. No end time or Zoom link: those are not part of the reminder.
   Two choices, because a downloaded .ics alone looked like nothing happened (it lands in Downloads)
   and does not work in the Instagram browser: Google opens a ready event in Google Calendar,
   Apple / Outlook downloads the .ics. */

function icsText(value) {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

function icsFold(line) {
  const parts = [];
  for (let rest = line; rest.length; rest = rest.slice(73)) parts.push(rest.slice(0, 73));
  return parts.join('\r\n ');
}

function icsUtc(date) {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function setUpCalendar(root) {
  const calendar = root.querySelector('[data-mvd-calendar]');
  const googleLink = root.querySelector('[data-mvd-calendar-google]');
  const icsLink = root.querySelector('[data-mvd-calendar-ics]');
  const { eventStart, eventDay, eventTime, pageUrl } = root.dataset;
  const start = new Date(eventStart);
  if (!calendar || !googleLink || !icsLink) return;
  if (Number.isNaN(start.getTime())) {
    calendar.hidden = true;
    return;
  }

  const title = 'Webinar reminder: How to Finally Learn Veterinary Dentistry the Right Way';
  const details = `Free live MortenVetDentistry webinar. Starts at ${eventTime}. Join via the Zoom details in your registration email.`;

  googleLink.href = `https://calendar.google.com/calendar/render?${new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${icsUtc(start)}/${icsUtc(start)}`,
    details: `${details}\n${pageUrl}`,
  })}`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'PRODID:-//MortenVetDentistry//Webinar Reminder//EN',
    'BEGIN:VEVENT',
    `UID:mortenvetdentistry-webinar-${eventDay.replace(/-/g, '')}@mortenvetdentist.com`,
    `DTSTAMP:${icsUtc(new Date())}`,
    `DTSTART:${icsUtc(start)}`,
    `SUMMARY:${icsText(title)}`,
    `DESCRIPTION:${icsText(details)}`,
    `URL:${pageUrl}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${icsText('Your MortenVetDentistry webinar starts in 15 minutes.')}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  icsLink.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.map(icsFold).join('\r\n') + '\r\n')}`;
  icsLink.download = `morten-vet-dentistry-webinar-reminder-${eventDay}.ics`;
}

/* Kit registration form: phone with country code and validation, SMS consent text with legal links. */

function linkedSmsDisclosure(label, { privacyUrl, termsUrl }) {
  const privacyLink = document.createElement('a');
  privacyLink.href = privacyUrl;
  privacyLink.target = '_blank';
  privacyLink.rel = 'noopener';
  privacyLink.textContent = 'Privacy Policy';

  const termsLink = document.createElement('a');
  termsLink.href = termsUrl;
  termsLink.target = '_blank';
  termsLink.rel = 'noopener';
  termsLink.textContent = 'Terms of Service';

  label.replaceChildren(
    'Add your mobile number only if you want SMS reminders. By adding it, you agree to receive Webinar Reminder SMS messages from MortenVetDentistry. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance. View our ',
    privacyLink,
    ' and ',
    termsLink,
    '.'
  );
}

function enhanceKitRegistration(root, { parsePhoneNumberFromString }) {
  const { kitUid, smsTag } = root.dataset;
  const registrationForm = root.querySelector(`.kit-embed-slot form[data-uid="${kitUid}"]`);
  if (!registrationForm || registrationForm.dataset.webinarEnhanced === 'true') return Boolean(registrationForm);

  const phoneNumber = registrationForm.querySelector('input[name="fields[phone_number_phone_number]"]');
  const smsConsent = registrationForm.querySelector(`input[name="tags[]"][value="${smsTag}"]`);
  const smsLabel = smsConsent && registrationForm.querySelector(`label[for="${smsConsent.id}"]`);
  if (!phoneNumber || !smsConsent || !smsLabel) return false;

  registrationForm.dataset.webinarEnhanced = 'true';
  registrationForm.classList.add('kit-registration-form');
  registrationForm.querySelector('.formkit-submit > span').textContent = 'Register for the webinar';
  phoneNumber.id = 'kit-phone-number';
  phoneNumber.type = 'tel';
  phoneNumber.autocomplete = 'tel-national';
  phoneNumber.inputMode = 'tel';
  phoneNumber.placeholder = 'Mobile for an SMS reminder';
  phoneNumber.setAttribute('aria-label', 'Mobile number for an SMS reminder (optional)');

  // Entering a mobile number is the SMS opt-in: the consent checkbox is hidden and set on submit,
  // so Kit still applies the SMS consent tag. Only its wording shows, under the phone field.
  linkedSmsDisclosure(smsLabel, root.dataset);
  smsConsent.closest('.formkit-checkboxes')?.classList.add('sms-consent-auto');
  smsConsent.tabIndex = -1;
  smsConsent.checked = false;

  const phoneField = phoneNumber.closest('.formkit-field');
  const phoneRow = document.createElement('div');
  phoneRow.className = 'phone-input-row';
  const countryCode = document.createElement('select');
  countryCode.className = 'country-code-select';
  countryCode.name = 'phone_country_code';
  countryCode.autocomplete = 'tel-country-code';
  countryCode.setAttribute('aria-label', 'Country calling code');
  countryCode.add(new Option('Country / code', ''));
  for (const { country, name, code } of countryOptions) countryCode.add(new Option(`${name} +${code}`, country));

  const phoneError = document.createElement('p');
  phoneError.className = 'phone-validation-error';
  phoneError.id = 'phone-validation-error';
  phoneError.setAttribute('aria-live', 'polite');
  phoneNumber.setAttribute('aria-describedby', phoneError.id);
  phoneField.replaceChildren(phoneRow, phoneError);
  phoneRow.append(countryCode, phoneNumber);

  function showPhoneError(message) {
    phoneError.textContent = message;
    phoneNumber.setCustomValidity(message);
  }

  function clearPhoneError() {
    phoneError.textContent = '';
    phoneNumber.setCustomValidity('');
  }

  function validatePhoneNumber({ announce = false } = {}) {
    const rawPhone = phoneNumber.value.trim();
    if (!rawPhone) {
      clearPhoneError();
      return { valid: true };
    }

    if (!countryCode.value) {
      const message = 'Select a country calling code to validate your phone number.';
      if (announce) showPhoneError(message);
      return { valid: false, message };
    }

    const parsed = parsePhoneNumberFromString(rawPhone, countryCode.value);
    if (!parsed || !parsed.isValid()) {
      const message = 'Enter a valid phone number for the selected country.';
      if (announce) showPhoneError(message);
      return { valid: false, message };
    }

    phoneNumber.value = parsed.number;
    clearPhoneError();
    return { valid: true };
  }

  // Kit overwrites a returning subscriber's saved phone number with an empty value, so a blank phone
  // field is left out of the submission and put back as soon as someone types in it.
  const phoneFieldName = phoneNumber.name;
  phoneNumber.addEventListener('input', () => { phoneNumber.name = phoneFieldName; });
  phoneNumber.addEventListener('blur', () => validatePhoneNumber({ announce: true }));
  countryCode.addEventListener('change', () => {
    if (phoneNumber.value.trim()) validatePhoneNumber({ announce: true });
  });
  registrationForm.addEventListener('submit', (event) => {
    const result = validatePhoneNumber({ announce: true });
    if (!result.valid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      phoneNumber.focus();
      return;
    }
    const hasPhone = Boolean(phoneNumber.value.trim());
    smsConsent.checked = hasPhone;
    if (!hasPhone) phoneNumber.removeAttribute('name');
  }, true);
  return true;
}

// The theme editor inserts re-rendered sections with innerHTML, so the Kit <script> in the new
// markup never runs. Swap it for a live copy (one script per slot, never a second one).
function reviveKitScript(root) {
  const slot = root.querySelector('.kit-embed-slot');
  const { kitUid } = root.dataset;
  if (!slot || !kitUid || slot.querySelector('form[data-uid]')) return;
  const script = document.createElement('script');
  script.async = true;
  script.dataset.uid = kitUid;
  script.src = KIT_SCRIPT(kitUid);
  const inert = slot.querySelector('script[data-uid]');
  if (inert) inert.replaceWith(script);
  else slot.append(script);
}

async function setUpRegistration(root) {
  const lib = await loadPhoneLibrary();
  if (!root.isConnected || enhanceKitRegistration(root, lib)) return;

  const slot = root.querySelector('.kit-embed-slot');
  const observer = new MutationObserver(() => {
    if (enhanceKitRegistration(root, lib)) {
      observer.disconnect();
      observers.delete(root);
    }
  });
  observer.observe(slot, { childList: true, subtree: true });
  observers.set(root, observer);
}

function init(root, { rerendered = false } = {}) {
  setUpCalendar(root);
  if (rerendered) reviveKitScript(root);
  setUpRegistration(root).catch((error) => console.error('Webinar form enhancement failed', error));
}

function teardown(root) {
  observers.get(root)?.disconnect();
  observers.delete(root);
}

document.querySelectorAll('[data-mvd-webinar]').forEach((root) => init(root));

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-mvd-webinar]').forEach((root) => init(root, { rerendered: true }));
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-mvd-webinar]').forEach(teardown);
});
